// Libraries
import { VictoryStyleInterface, VictoryThemeDefinition } from 'victory';
// Types
import { RatingKeys } from '@globalTypes/index';
// Utils
import { LineVariationKeys, ScatterPointVariationKeys } from '@organisms/index';
import { colors, fonts } from '@themeFoundations/index';
import { generateAreaVariations, generateLineVariations, generatePointScatterVariations } from './generators';

/**
 * Represents the primary color scale for ratings.
 */
const colorsScalePrimary = [
  colors.ratings.A.dark,
  colors.ratings.B.dark,
  colors.ratings.C.dark,
  colors.ratings.D.dark,
  colors.ratings.E.dark,
];

/**
 * Represents the opacity scale for ratings.
 */
export const colorsScaleOpaqued = [
  colors.ratings.A.opaqued,
  colors.ratings.B.opaqued,
  colors.ratings.C.opaqued,
  colors.ratings.D.opaqued,
  colors.ratings.E.opaqued,
];

/**
 * Represents the base label styles.
 */
export const baseLabelStyles = {
  fontFamily: fonts.body,
  letterSpacing: 'normal',
  padding: 10,
  stroke: 'transparent',
  fontSize: 10,
  fill: colors.text.faded,
};

/**
 * Represents the background for the label component.
 */
export const labelComponentBackground = {
  fill: 'transparent',
};

/**
 * Represents the padding for the label component.
 */
export const labelComponentPadding = {
  top: 0,
  left: 10,
};

/**
 * Represents the grid layout properties for showing the grid.
 */
export const gridLayoutStyle = {
  grid: {
    stroke: 'lightgrey',
  },
  axis: {
    stroke: 'transparent',
  },
};

/**
 * Represents the grid layout properties for showing the grid.
 */
export const greyAxis = {
  axis: {
    stroke: colors.grey[100],
  },
};

/**
 * Represents the grid layout properties for showing the grid.
 */
export const additionalAxisStyle = {
  axis: {
    stroke: 'url(#additional-axis-gradient)',
    strokeWidth: 3,
  },
};
/**
 * Represent the style for the Stack Bar Chart Axes.
 */
export const stackBarChartAxis = {
  axis: { stroke: 'transparent' },
  grid: { stroke: colors.grey[30] },
  ticks: {
    stroke: colors.grey[30],
    size: 10,
  },
  tickLabels: { ...baseLabelStyles },
};

/**
 * Represents the chart styles definition.
 */
export const cii: VictoryThemeDefinition = {
  axis: {
    style: {
      axis: {
        stroke: 'lightgrey',
      },
      grid: {
        stroke: 'transparent',
      },
      tickLabels: { ...baseLabelStyles },
      axisLabel: {
        fontSize: 10,
        fontWeight: 600,
        padding: 30,
        fill: colors.text.faded,
      },
    },
  },
  bar: {
    style: {
      data: {
        stroke: colors.whiteLilac,
        strokeWidth: 0.7,
      },
    },
  },
  stack: {
    colorScale: colorsScalePrimary,
  },
  line: {},
};

/**
 * The stroke values for the chart line style.
 */
const strokeWidth = 2;
const strokeDasharray = 5;

/**
 * Represents the line variations for the LineChart component.
 */
export const lineVariations: Partial<Record<LineVariationKeys, VictoryStyleInterface>> = {
  black: {
    data: {
      stroke: colors.blackBase,
      strokeWidth,
    },
    border: {},
  },
  blackDashed: {
    data: {
      stroke: colors.blackBase,
      strokeWidth,
      strokeDasharray,
    },
  },
  light: {
    data: {
      stroke: colors.base,
      strokeWidth,
    },
  },
  lightDashed: {
    data: {
      stroke: colors.base,
      strokeWidth,
      strokeDasharray,
    },
  },
  blackDotted: {
    data: {
      stroke: colors.blackBase,
      strokeWidth: 1.5,
      strokeDasharray: 3,
    },
  },
  ...generateLineVariations(strokeWidth, strokeDasharray),
};

/**
 * Represents the line variations for the LineChart component.
 */
export const areaVariations: Partial<Record<RatingKeys, VictoryStyleInterface>> = {
  ...generateAreaVariations(),
};

/**
 * Represents the line variations for the LineChart component.
 */
export const pointScatterVariations: Partial<Record<ScatterPointVariationKeys, VictoryStyleInterface>> = {
  yellow: {
    data: {
      stroke: colors.orange,
      fill: colors.orange,
      strokeWidth,
    },
  },
  blue: {
    data: {
      stroke: colors.base,
      fill: colors.base,
      strokeWidth,
    },
  },
  ...generatePointScatterVariations(strokeWidth, colors.snow),
};
